<template>
  <div id="app">
    <transition-group name="fade">

      <div class="wrap" v-show="startedQuiz === false" key="intro" >
        <div class="intro">
          <div class="intro-inner">
            <h1>Chemical Reactions and Stoichiometry</h1>
            <p>Classify each chemical reaction, then balance the equation and solve a stoichiometry question based on it.</p>
            <button class="btn" v-on:click="startedQuiz = true">Begin Quiz</button>
          </div>
        </div>
      </div>

      <quiz v-bind:questions="questions" v-on:restartQuiz="startedQuiz = false" v-show="startedQuiz === true" key="quizComponent"></quiz>

    </transition-group>
  </div>
</template>

<script>
import Quiz from './components/Quiz'

export default {
  name: 'app',
  components: {
    Quiz
  },
  data: function () {
    return {
      startedQuiz: false,
      questions: [
        {
          component: 'Equation0',
          first: 'synthesis',
          second: [2, 1, 2],
          questionA: 'Calculate the number of water (H<sub>2</sub>O) molecules formed if 0.562 moles of oxygen gas (O<sub>2</sub>) react.',
          answerA: ['6.77', '6.76'],
          answerTemplateA: 'x 10<sup>23</sup> <b>molecules</b>',
          questionB: 'Determine the moles of oxygen gas (O<sub>2</sub>) needed to completely react with 1.975 moles of hydrogen gas (H<sub>2</sub>).',
          answerB: ['0.9875', '.9875'],
          answerTemplateB: '<b>moles</b>'
        },
        {
          component: 'Equation1',
          first: 'synthesis',
          second: [4, 3, 2],
          questionA: 'Determine the number of moles of oxygen gas (O<sub>2</sub>) that are needed to react with 0.725 moles of aluminum (Al).',
          answerA: ['0.544', '.544'],
          answerTemplateA: '<b>moles</b>',
          questionB: 'Calculate the mass of aluminum oxide (Al<sub>2</sub>O<sub>3</sub>) produced if 25.00 g of Aluminum (Al) is reacted completely.',
          answerB: ['47.23', '47.24', '47.25'],
          answerTemplateB: '<b>grams</b>'
        },
        {
          component: 'Equation2',
          first: 'decomposition',
          second: [1, 1, 1],
          questionA: 'How many carbon dioxide (CO<sub>2</sub>) molecules are produced during the decomposition of 1.25 moles of Carbonic acid (H<sub>2</sub>CO<sub>3</sub>)?',
          answerA: ['7.53'],
          answerTemplateA: 'x 10<sup>23</sup> <b>molecules</b>',
          questionB: 'Determine the number of water (H<sub>2</sub>O) molecules produced by the reaction if 7.25 x 10<sup>23</sup> molecules of carbon dioxide (CO<sub>2</sub>) are made.',
          answerB: ['7.25'],
          answerTemplateB: 'x 10<sup>23</sup> <b>molecules</b>'
        },
        {
          component: 'Equation3',
          first: 'decomposition',
          second: [2, 1, 3],
          questionA: 'Determine the mass of Iron (III) oxide (Fe<sub>2</sub>O<sub>3</sub>) produced in the decomposition reaction of 100.0 grams of Iron (III) hydroxide (Fe(OH)<sub>3</sub>).',
          answerA: ['74.71'],
          answerTemplateA: '<b>grams</b>',
          questionB: 'Calculate the number of water (H<sub>2</sub>O) molecules produced from the decomposition of 75.50 grams of Iron (III) hydroxide (Fe(OH)<sub>3</sub>).',
          answerB: ['6.381'],
          answerTemplateB: 'x 10<sup>23</sup> <b>molecules</b>'
        },
        {
          component: 'Equation4',
          first: 'single replacement',
          second: [2, 1, 1, 2],
          questionA: 'Determine the mass of silver (Ag) produced in the reaction if 6.50x10<sup>23</sup> atoms of magnesium (Mg) react.',
          answerA: ['233'],
          answerTemplateA: '<b>grams</b>',
          questionB: 'Determine the mass of magnesium (Mg) needed to react with 0.955 moles of silver nitrate (AgNO<sub>3</sub>).',
          answerB: ['11.6'],
          answerTemplateB: '<b>grams</b>'
        },
        {
          component: 'Equation5',
          first: 'single replacement',
          second: [2, 3, 2, 1],
          questionA: 'Calculate the mass of aluminum (Al) produced if 0.862 moles of Magnesium (Mg) react.',
          answerA: ['15.5'],
          answerTemplateA: '<b>grams</b>',
          questionB: 'Determine the mass of aluminum (Al) produced in the reaction if 30.00 grams of magnesium (Mg) was consumed.',
          answerB: ['22.20'],
          answerTemplateB: '<b>grams</b>'
        },
        {
          component: 'Equation6',
          first: 'double replacement',
          second: [1, 1, 1, 2],
          questionA: 'Calculate the moles of calcium chloride (CaCl<sub>2</sub>) needed to react in order to produce 85.00 grams of calcium carbonate (CaCO<sub>3</sub>).',
          answerA: ['0.8492', '.8492', '0.8493', '.8493'],
          answerTemplateA: '<b>moles</b>',
          questionB: 'Calculate the number of moles of sodium chloride (NaCl) formed if 0.345 moles of calcium chloride (CaCl<sub>2</sub>) react.',
          answerB: ['0.690', '.690'],
          answerTemplateB: '<b>moles</b>'
        },
        {
          component: 'Equation7',
          first: 'double replacement',
          second: [2, 1, 2, 1],
          questionA: 'If 175.0 grams of lead (II) iodide (PbI<sub>2</sub>) is produced from the reaction, how many moles of potassium nitrate (KNO3) are produced?',
          answerA: ['0.7592', '.7592'],
          answerTemplateA: '<b>moles</b>',
          questionB: 'If 0.455 moles of potassium iodide (KI) react, what mass of lead (II) iodide (PbI<sub>2</sub>) will be produced?',
          answerB: ['105'],
          answerTemplateB: '<b>grams</b>'
        },
        {
          component: 'Equation8',
          first: 'combustion',
          second: [1, 5, 3, 4],
          questionA: 'Determine how many moles of water (H<sub>2</sub>O) are formed from the combustion of 7.50 x 10<sup>23</sup> molecules of propane (C<sub>3</sub>H<sub>8</sub>). ',
          answerA: ['4.98'],
          answerTemplateA: '<b>moles</b>',
          questionB: 'Calculate the number of carbon dioxide (CO<sub>2</sub>) molecules produced in the reaction if 0.7525 moles of oxygen gas (O<sub>2</sub>) are consumed.',
          answerB: ['2.719'],
          answerTemplateB: 'x 10<sup>23</sup> <b>molecules</b>'
        },
        {
          component: 'Equation9',
          first: 'combustion',
          second: [2, 7, 4, 6],
          questionA: 'Determine how many carbon dioxide (CO<sub>2</sub>) molecules are produced if 8.45 x 10<sup>23</sup> molecules of water (H<sub>2</sub>O) are produced',
          answerA: ['5.63'],
          answerTemplateA: 'x 10<sup>23</sup> <b>molecules</b>',
          questionB: 'Calculate the number of moles of oxygen gas (O<sub>2</sub>) needed to fully react with 9.75x10<sup>23</sup> molecules of ethane (C<sub>2</sub>H<sub>6</sub>). ',
          answerB: ['5.67'],
          answerTemplateB: '<b>moles</b>'
        }
      ]
    }
  }
}
</script>

<style lang="scss">
@import "assets/styles.scss";
.intro {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  h1 {
    font: bold 32px $font-family;
    color: $dk_grey;
    margin: 0 0 16px;
    letter-spacing: 1.5px;
  }
  p br { display: none; }
  .intro-inner { position: relative; }
}
@media screen and (min-width: 600px) {
  .intro p br { display: block; }
}

.fade-enter-active, .fade-leave-active {
  transition-property: opacity;
  transition-duration: 0.25s;
}
.fade-enter-active {
  transition-delay: 0.35s;
}
.fade-enter, .fade-leave-active {
  opacity: 0;
}
.btn {
  border: none;
  background: $blue;
  color: #fff;
  padding: 10px 40px;
  text-transform: uppercase;
  font: bold 16px $font-family;
  letter-spacing: 2px;
  cursor: pointer;
}
p { 
  color: $dk_grey;
  font-size: 20px;
  letter-spacing: 1.5px;
  margin: 0 0 20px;
}
</style>


// WEBPACK FOOTER //
// App.vue?248307c0